import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  type PlatformMessage,
  listMyMessages,
  getMyReadMessageIds,
  markMessageRead,
  markAllMessagesRead,
} from '../../services/platformMessageService';

const PAGE_SIZE = 5;

interface MessageListModalProps {
  onClose: () => void;
  onUnreadCountChange?: (count: number) => void;
}

function formatMessageDate(isoStr: string): string {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) {
    return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays < 7) return `${diffDays}일 전`;
  return d.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

const MessageListModal: React.FC<MessageListModalProps> = ({ onClose, onUnreadCountChange }) => {
  const [messages, setMessages] = useState<PlatformMessage[]>([]);
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0); // 0-based
  const backdropRef = useRef<HTMLDivElement>(null);
  const listScrollRef = useRef<HTMLUListElement>(null);
  const itemRefs = useRef<Map<string, HTMLLIElement>>(new Map());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const msgs = await listMyMessages();
      setMessages(msgs);
      const ids = await getMyReadMessageIds(msgs.map((m) => m.id));
      setReadIds(ids);
      onUnreadCountChange?.(msgs.filter((m) => !ids.has(m.id)).length);
    } catch (e) {
      setError(e instanceof Error ? e.message : '불러오기 실패');
    } finally {
      setLoading(false);
    }
  }, [onUnreadCountChange]);

  useEffect(() => { load(); }, [load]);

  // 바깥 클릭 닫기
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (backdropRef.current && e.target === backdropRef.current) onClose();
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [onClose]);

  // ESC 닫기
  useEffect(() => {
    const handle = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [onClose]);

  // 확장된 메시지가 목록 상단에 보이도록 스크롤
  useEffect(() => {
    if (!expandedId) return;
    const list = listScrollRef.current;
    const el = itemRefs.current.get(expandedId);
    if (!list || !el) return;
    requestAnimationFrame(() => {
      const listTop = list.getBoundingClientRect().top;
      const elTop = el.getBoundingClientRect().top;
      const nextTop = list.scrollTop + (elTop - listTop);
      list.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' });
    });
  }, [expandedId]);

  const handleToggle = useCallback(async (msg: PlatformMessage) => {
    if (expandedId === msg.id) { setExpandedId(null); return; }
    setExpandedId(msg.id);
    if (!readIds.has(msg.id)) {
      try {
        await markMessageRead(msg.id);
        setReadIds((prev) => new Set(Array.from(prev).concat(msg.id)));
        onUnreadCountChange?.(
          messages.filter((m) => !readIds.has(m.id) && m.id !== msg.id).length
        );
      } catch { /* ignore */ }
    }
  }, [expandedId, readIds, messages, onUnreadCountChange]);

  const handleMarkAllRead = useCallback(async () => {
    const unread = messages.filter((m) => !readIds.has(m.id));
    if (!unread.length) return;
    try {
      await markAllMessagesRead(unread.map((m) => m.id));
      setReadIds(new Set(messages.map((m) => m.id)));
      onUnreadCountChange?.(0);
    } catch { /* ignore */ }
  }, [messages, readIds, onUnreadCountChange]);

  const setItemRef = useCallback((id: string, node: HTMLLIElement | null) => {
    if (node) itemRefs.current.set(id, node);
    else itemRefs.current.delete(id);
  }, []);

  const unreadCount = messages.filter((m) => !readIds.has(m.id)).length;
  const totalPages = Math.ceil(messages.length / PAGE_SIZE);
  const pageMsgs = messages.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const showPagination = !loading && !error && totalPages > 1;

  const modal = (
    <div className="cp-msg-modal-backdrop" ref={backdropRef} aria-modal="true" role="dialog" aria-label="메시지 수신함">
      <div className={'cp-msg-modal' + (showPagination ? ' has-pagination' : '')}>
        <div className="cp-msg-modal-header">
          <span className="cp-msg-modal-title">
            메시지 수신함
            {!loading && unreadCount > 0 && (
              <span className="cp-msg-modal-header-badge">{unreadCount}</span>
            )}
          </span>
          <div className="cp-msg-modal-header-actions">
            {!loading && unreadCount > 0 && (
              <button type="button" className="cp-msg-modal-mark-all-btn" onClick={handleMarkAllRead}>
                모두 읽음
              </button>
            )}
            <button type="button" className="cp-msg-modal-close-btn" onClick={onClose} aria-label="닫기">
              ✕
            </button>
          </div>
        </div>

        <div className="cp-msg-modal-body">
          {!loading && error && (
            <div className="cp-msg-modal-status cp-msg-modal-status--error">{error}</div>
          )}
          {!loading && !error && messages.length === 0 && (
            <div className="cp-msg-modal-status">받은 메시지가 없습니다.</div>
          )}
          {!loading && !error && messages.length > 0 && (
            <>
              <ul className="cp-msg-modal-list" ref={listScrollRef}>
                {pageMsgs.map((msg) => {
                  const isRead = readIds.has(msg.id);
                  const isExpanded = expandedId === msg.id;
                  return (
                    <li
                      key={msg.id}
                      ref={(node) => setItemRef(msg.id, node)}
                      className={
                        'cp-msg-modal-item' +
                        (isExpanded ? ' is-expanded' : '') +
                        (isRead ? ' is-read' : ' is-unread')
                      }
                    >
                      <button
                        type="button"
                        className="cp-msg-modal-item-header"
                        onClick={() => handleToggle(msg)}
                        aria-expanded={isExpanded}
                      >
                        <span className="cp-msg-modal-item-left">
                          {!isRead && <span className="cp-msg-modal-item-dot" aria-label="읽지 않음" />}
                          <span className="cp-msg-modal-item-preview-text">
                            {msg.body.slice(0, 40)}{msg.body.length > 40 ? '…' : ''}
                          </span>
                        </span>
                        <span className="cp-msg-modal-item-right">
                          <span className="cp-msg-modal-item-date">{formatMessageDate(msg.created_at)}</span>
                          <span className="cp-msg-modal-item-chevron" aria-hidden="true">
                            {isExpanded ? '▲' : '▽'}
                          </span>
                        </span>
                      </button>
                      {isExpanded && (
                        <div className="cp-msg-modal-item-body">{msg.body}</div>
                      )}
                    </li>
                  );
                })}
              </ul>

              {totalPages > 1 && (
                <div className="cp-msg-modal-pagination">
                  <button
                    type="button"
                    className="cp-msg-modal-page-btn"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    aria-label="이전 페이지"
                  >
                    ‹
                  </button>
                  <span className="cp-msg-modal-page-info">{page + 1} / {totalPages}</span>
                  <button
                    type="button"
                    className="cp-msg-modal-page-btn"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    aria-label="다음 페이지"
                  >
                    ›
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default MessageListModal;
